import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";

export interface ChatMessage {
  id: number;
  /** Null once the author deleted their account: the line stays, the name goes. */
  author: { id: number; public_name: string } | null;
  body: string;
  created_at: string;
}

export interface ChatPage {
  results: ChatMessage[];
  has_more: boolean;
}

export const chatKey = (teamId: number) => ["chat", teamId] as const;

/** The latest page of the team's thread, oldest first. Pushed frames extend it (ADR 0029). */
export function useChatMessages(teamId: number) {
  return useQuery({
    queryKey: chatKey(teamId),
    queryFn: () => api<ChatPage>(`/api/teams/${teamId}/chat/messages/`),
    staleTime: Infinity,
  });
}

/** Adds a message once: the sender gets it from the POST reply and from the socket. */
export function appendChatMessage(qc: QueryClient, teamId: number, message: ChatMessage): void {
  qc.setQueryData<ChatPage>(chatKey(teamId), (old) => {
    if (!old) return old;
    if (old.results.some((existing) => existing.id === message.id)) return old;
    return { ...old, results: [...old.results, message] };
  });
}

export function useSendChatMessage(teamId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<ChatMessage>(`/api/teams/${teamId}/chat/messages/`, { method: "POST", json: { body } }),
    onSuccess: (message) => appendChatMessage(qc, teamId, message),
  });
}
